<template>
    <!-- Modal Backdrop -->
    <div 
        class="modal-backdrop fade" 
        :class="{ 'show': isVisible }"
        v-if="isVisible"
        @click="handleBackdropClick"
    ></div>

    <!-- Modal -->
    <div 
        class="modal fade" 
        :class="{ 'show d-block': isVisible }" 
        tabindex="-1" 
        role="dialog"
        v-show="isVisible"
    >
        <div class="modal-dialog modal-dialog-centered modal-lg" :class="modalSize">
            <div class="modal-content admin-modal">
                <!-- Modal Header -->
                <div class="modal-header">
                    <h5 class="modal-title">
                        <slot name="title">
                        {{ title }}
                        </slot>
                    </h5>
                    <button
                        type="button"
                        class="modal-close"
                        @click="closeModal"
                        aria-label="Close"
                    >
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body">
                    <slot name="body">
                        <div v-html="content"></div>
                    </slot>
                </div>

                <!-- Modal Footer -->
                <div class="modal-footer">
                    <slot name="footer">
                        <button
                            type="button"
                            class="btn-ghost"
                            @click="closeModal"
                        >
                            {{ $t('modal2.cancel') }}
                        </button>
                        <button
                            type="button"
                            class="btn-danger-soft"
                            @click="handleConfirm"
                        >
                            <i class="bi bi-trash"></i>
                            <span>{{ $t('modal2.delete') }}</span>
                        </button>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BootstrapModal',
    props: {
        // Control visibility from parent
        modelValue: {
            type: Boolean,
            default: false
        },
        // Modal title
        title: {
            type: String,
            default: 'Modal Title'
        },
        // Default content if no slot is provided
        content: {
            type: String,
            default: 'Default modal content'
        },
        // Modal size options: sm, lg, xl, or empty for default
        size: {
            type: String,
            default: '',
            validator: (value) => ['', 'sm', 'lg', 'xl'].includes(value)
        },
        // Close modal when clicking backdrop
        closeOnBackdrop: {
            type: Boolean,
            default: true
        },
        // Show close button in header
        showCloseButton: {
            type: Boolean,
            default: true
        },
        index: {
            type: Number,
            default: null
        }
    },
    emits: ['update:modelValue', 'close', 'confirm', 'opened'],
    data() {
        return {
            isVisible: this.modelValue
        }
    },
    computed: {
        modalSize() {
            return this.size ? `modal-${this.size}` : ''
        }
    },
    watch: {
        modelValue(newVal) {
            this.isVisible = newVal
            if (newVal) {
                this.disableBodyScroll()
                this.$emit('opened')
            } else {
                this.enableBodyScroll()
            }
        },
        isVisible(newVal) {
        this.$emit('update:modelValue', newVal)
            if (!newVal) {
                this.$emit('close')
            }
        }
    },
    methods: {
        openModal() {
            this.isVisible = true
        },
        closeModal() {
            this.isVisible = false
        },
        handleConfirm() {
            this.$emit('confirm', this.index)
            this.closeModal()
        },
        handleBackdropClick() {
            if (this.closeOnBackdrop) {
                this.closeModal()
            }
        },
        disableBodyScroll() {
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = '15px' // Prevent content shift from scrollbar
        },
        enableBodyScroll() {
            document.body.style.overflow = 'auto'
            document.body.style.paddingRight = '0'
        },
        // Handle ESC key press
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.isVisible) {
                this.closeModal()
            }
        }
    },
    mounted() {
        // Add event listener for ESC key
        document.addEventListener('keydown', this.handleEscapeKey)
        
        if (this.isVisible) {
            this.disableBodyScroll()
        }
    },
    beforeUnmount() {
        // Clean up event listeners
        document.removeEventListener('keydown', this.handleEscapeKey)
        this.enableBodyScroll()
    }
}
</script>

<style scoped>
.modal {
    background-color: rgba(15, 23, 42, 0.5);
    z-index: 1050;
}

.modal-backdrop {
    z-index: 1040;
}

.modal.show {
    opacity: 1;
}

.admin-modal {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12);
    overflow: hidden;
}

.admin-modal .modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    align-items: center;
}

.admin-modal .modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.admin-modal .modal-close {
    background: transparent;
    border: 1px solid #e5e7eb;
    color: #64748b;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    padding: 0;
}
.admin-modal .modal-close:hover {
    background: #f8fafc;
    color: #0f172a;
}

.admin-modal .modal-body {
    padding: 24px;
    color: #1e293b;
    font-size: 14px;
}

.admin-modal .modal-footer {
    padding: 18px 24px;
    border-top: 1px solid #e5e7eb;
    background: #fafbfc;
    gap: 10px;
}

.btn-danger-soft {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #991b1b;
    padding: 10px 18px;
    border-radius: 10px;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.btn-danger-soft:hover {
    background: #fecaca;
}
</style>